/**
 * GroupDealSettlements - Admin Panel
 * Manage group deal payments and supplier payouts
 * CSS prefix: gds- (group deal settlements)
 */

import React, { useState, useEffect } from 'react';
import {
  Wallet,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  Clock,
  XCircle,
  Eye,
  FileDown,
  X,
  Loader2,
  TrendingUp,
  Package,
  CreditCard,
  Users,
  Store
} from 'lucide-react';

const GroupDealSettlements = () => {
  // Main tab: 'payments' or 'settlements'
  const [mainTab, setMainTab] = useState('payments');

  // Payments state
  const [payments, setPayments] = useState([]);
  const [paymentsPagination, setPaymentsPagination] = useState({ current: 1, pages: 1, total: 0 });

  // Settlements state
  const [settlements, setSettlements] = useState([]);
  const [settlementsPagination, setSettlementsPagination] = useState({ current: 1, pages: 1, total: 0 });
  const [settlementsView, setSettlementsView] = useState('all');

  // Shared state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('payment'); // 'payment' or 'settlement'
  const [processing, setProcessing] = useState(false);

  const loadPayments = async () => {
    try {
      setLoading(true);
      setError('');

      const queryParams = new URLSearchParams({
        page: paymentsPagination.current,
        limit: 20
      });

      const response = await fetch(`/api/admin/group-deals/payments?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        setPayments(data.payments || []);
        if (data.pagination) {
          setPaymentsPagination({
            current: data.pagination.page || 1,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          });
        }
      } else {
        setError(data.message || 'Failed to load payments');
      }
    } catch (err) {
      setError('Failed to load payments');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadSettlements = async () => {
    try {
      setLoading(true);
      setError('');

      let statusFilter = '';
      if (settlementsView === 'pending') statusFilter = 'PENDING';
      else if (settlementsView === 'settled') statusFilter = 'SETTLED';
      else if (settlementsView === 'failed') statusFilter = 'FAILED';

      const queryParams = new URLSearchParams({
        page: settlementsPagination.current,
        limit: 20,
        ...(statusFilter && { status: statusFilter })
      });

      const response = await fetch(`/api/admin/group-deals/settlements?${queryParams}`, {
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        setSettlements(data.settlements || []);
        if (data.pagination) {
          setSettlementsPagination({
            current: data.pagination.page || 1,
            pages: data.pagination.pages || 1,
            total: data.pagination.total || 0
          });
        }
      } else {
        setError(data.message || 'Failed to load settlements');
      }
    } catch (err) {
      setError('Failed to load settlements');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await fetch('/api/admin/group-deals/stats', {
        credentials: 'include'
      });
      const data = await response.json();

      if (response.ok) {
        setStats(data);
      }
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const runCleanup = async () => {
    try {
      setProcessing(true);
      const response = await fetch('/api/admin/group-deals/cleanup', {
        method: 'POST',
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        setSuccess(`Cleanup completed: ${data.result.completedMarked} deals completed, ${data.result.expiredMarked} expired`);
        setTimeout(() => setSuccess(''), 5000);
        loadStats();
        loadSettlements();
      } else {
        setError(data.message || 'Cleanup failed');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Failed to run cleanup');
      setTimeout(() => setError(''), 3000);
    } finally {
      setProcessing(false);
    }
  };

  const processSettlement = async (settlementId) => {
    try {
      setProcessing(true);
      const response = await fetch(`/api/admin/group-deals/settlements/${settlementId}/process`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Settlement processing initiated');
        setTimeout(() => setSuccess(''), 3000);
        loadSettlements();
        loadStats();
      } else {
        setError(data.message || 'Failed to process settlement');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Failed to process settlement');
      setTimeout(() => setError(''), 3000);
    } finally {
      setProcessing(false);
    }
  };

  const markAsSettled = async (settlementId) => {
    try {
      setProcessing(true);
      const response = await fetch(`/api/admin/group-deals/settlements/${settlementId}/mark-settled`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settlementMethod: 'MANUAL', note: 'Manually marked as settled' })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Settlement marked as settled');
        setTimeout(() => setSuccess(''), 3000);
        loadSettlements();
        loadStats();
        closeModal();
      } else {
        setError(data.message || 'Failed to mark settlement');
        setTimeout(() => setError(''), 5000);
      }
    } catch (err) {
      setError('Failed to mark settlement');
      setTimeout(() => setError(''), 3000);
    } finally {
      setProcessing(false);
    }
  };

  const exportToCSV = () => {
    const dataToExport = mainTab === 'payments' ? payments : settlements;
    if (dataToExport.length === 0) {
      setError('No data to export');
      setTimeout(() => setError(''), 3000);
      return;
    }

    let headers, rows;
    if (mainTab === 'payments') {
      headers = ['Payment ID', 'Merchant', 'Store', 'Deal', 'Supplier', 'Quantity', 'Amount Paid', 'Paid At'];
      rows = payments.map(p => [
        p.id,
        `${p.merchant?.firstName || ''} ${p.merchant?.lastName || ''}`.trim() || p.merchant?.email,
        p.merchant?.store?.name || 'N/A',
        p.deal?.title || 'N/A',
        p.deal?.supplier?.businessName || 'N/A',
        p.quantity,
        p.amountPaid,
        p.paidAt ? new Date(p.paidAt).toLocaleString() : 'N/A'
      ]);
    } else {
      headers = ['Settlement ID', 'Deal', 'Supplier', 'Total Collected', 'Platform Fee', 'Supplier Payout', 'Status', 'Created'];
      rows = settlements.map(s => [
        s.id,
        s.deal?.title || 'N/A',
        s.supplier?.businessName || 'N/A',
        s.totalCollected,
        s.platformFee,
        s.supplierPayout,
        s.status,
        new Date(s.createdAt).toLocaleString()
      ]);
    }

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `group-deal-${mainTab}-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    setSuccess('Exported successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN' }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      PENDING: { bg: '#fef3c7', color: '#92400e', icon: Clock },
      PROCESSING: { bg: '#dbeafe', color: '#1d4ed8', icon: Loader2 },
      SETTLED: { bg: '#dcfce7', color: '#166534', icon: CheckCircle2 },
      PAID: { bg: '#dcfce7', color: '#166534', icon: CheckCircle2 },
      FAILED: { bg: '#fee2e2', color: '#dc2626', icon: XCircle }
    };
    const style = statusStyles[status] || statusStyles.PENDING;
    const Icon = style.icon;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '4px 10px', background: style.bg, color: style.color,
        borderRadius: '9999px', fontSize: '12px', fontWeight: '500'
      }}>
        <Icon size={12} className={status === 'PROCESSING' ? 'animate-spin' : ''} />
        {status.toLowerCase()}
      </span>
    );
  };

  const viewPaymentDetails = (payment) => {
    setSelectedItem(payment);
    setModalType('payment');
    setShowModal(true);
  };

  const viewSettlementDetails = async (settlement) => {
    try {
      const response = await fetch(`/api/admin/group-deals/settlements/${settlement.id}`, {
        credentials: 'include'
      });
      const data = await response.json();
      if (response.ok) {
        setSelectedItem(data.settlement);
        setModalType('settlement');
        setShowModal(true);
      }
    } catch (err) {
      setError('Failed to load details');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedItem(null);
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    if (mainTab === 'payments') {
      loadPayments();
    } else {
      loadSettlements();
    }
  }, [mainTab, paymentsPagination.current, settlementsPagination.current, settlementsView]);

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#111827', margin: 0 }}>
            Group Deal Finance
          </h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={runCleanup}
              disabled={processing}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px',
                background: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px',
                cursor: processing ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500'
              }}
            >
              <RefreshCw size={16} className={processing ? 'animate-spin' : ''} />
              Process Completed Deals
            </button>
            <button
              onClick={() => { loadStats(); mainTab === 'payments' ? loadPayments() : loadSettlements(); }}
              disabled={loading}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px',
                background: 'white', border: '1px solid #e5e7eb', borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500', color: '#374151'
              }}
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
            <button
              onClick={exportToCSV}
              style={{
                display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px',
                background: '#10b981', color: 'white', border: 'none', borderRadius: '8px',
                cursor: 'pointer', fontSize: '14px', fontWeight: '600'
              }}
            >
              <FileDown size={16} />
              Export CSV
            </button>
          </div>
        </div>
        <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
          View merchant payments and manage supplier payouts
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{
          padding: '12px 16px', background: '#fef2f2', border: '1px solid #fee2e2',
          borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626'
        }}>
          <AlertCircle size={18} />{error}
        </div>
      )}
      {success && (
        <div style={{
          padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0',
          borderRadius: '8px', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px', color: '#16a34a'
        }}>
          <CheckCircle2 size={18} />{success}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#dbeafe', borderRadius: '10px' }}>
              <CreditCard size={20} color="#2563eb" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Total Payments</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
            {formatCurrency(stats?.payments?.totalPaid || 0)}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
            {stats?.payments?.count || 0} merchants paid
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#fef3c7', borderRadius: '10px' }}>
              <Clock size={20} color="#d97706" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Pending Settlements</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>
            {stats?.settlements?.pending || 0}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
            awaiting payout
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: '#dcfce7', borderRadius: '10px' }}>
              <CheckCircle2 size={20} color="#16a34a" />
            </div>
            <span style={{ color: '#6b7280', fontSize: '14px' }}>Supplier Payouts</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700', color: '#16a34a' }}>
            {formatCurrency(stats?.settlements?.supplierPayout || 0)}
          </div>
          <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>
            paid to suppliers
          </div>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
          borderRadius: '12px', padding: '20px', color: 'white'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
            <div style={{ padding: '10px', background: 'rgba(255,255,255,0.2)', borderRadius: '10px' }}>
              <TrendingUp size={20} color="white" />
            </div>
            <span style={{ fontSize: '14px', opacity: 0.9 }}>Platform Revenue</span>
          </div>
          <div style={{ fontSize: '24px', fontWeight: '700' }}>
            {formatCurrency(stats?.settlements?.platformFee || 0)}
          </div>
          <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '4px' }}>
            Platform fees earned
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <div style={{ display: 'flex', gap: '4px', background: '#f3f4f6', padding: '4px', borderRadius: '12px', marginBottom: '16px', width: 'fit-content' }}>
        <button
          onClick={() => setMainTab('payments')}
          style={{
            padding: '10px 20px', borderRadius: '10px', border: 'none',
            background: mainTab === 'payments' ? 'white' : 'transparent',
            color: mainTab === 'payments' ? '#059669' : '#6b7280',
            fontWeight: mainTab === 'payments' ? '600' : '500',
            boxShadow: mainTab === 'payments' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px'
          }}
        >
          <CreditCard size={16} />
          Merchant Payments
          {stats?.payments?.count > 0 && (
            <span style={{ background: '#dcfce7', color: '#059669', padding: '2px 8px', borderRadius: '9999px', fontSize: '12px' }}>
              {stats.payments.count}
            </span>
          )}
        </button>
        <button
          onClick={() => setMainTab('settlements')}
          style={{
            padding: '10px 20px', borderRadius: '10px', border: 'none',
            background: mainTab === 'settlements' ? 'white' : 'transparent',
            color: mainTab === 'settlements' ? '#059669' : '#6b7280',
            fontWeight: mainTab === 'settlements' ? '600' : '500',
            boxShadow: mainTab === 'settlements' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
            cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px'
          }}
        >
          <Wallet size={16} />
          Supplier Settlements
          {stats?.settlements?.pending > 0 && (
            <span style={{ background: '#fef3c7', color: '#d97706', padding: '2px 8px', borderRadius: '9999px', fontSize: '12px' }}>
              {stats.settlements.pending} pending
            </span>
          )}
        </button>
      </div>

      {/* Settlement Sub-tabs */}
      {mainTab === 'settlements' && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          {[
            { id: 'all', label: 'All', icon: Package },
            { id: 'pending', label: 'Pending', icon: Clock },
            { id: 'settled', label: 'Settled', icon: CheckCircle2 },
            { id: 'failed', label: 'Failed', icon: XCircle }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setSettlementsView(tab.id); setSettlementsPagination(p => ({ ...p, current: 1 })); }}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
                background: settlementsView === tab.id ? '#111827' : 'transparent',
                color: settlementsView === tab.id ? 'white' : '#6b7280',
                border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500'
              }}
            >
              <tab.icon size={16} />{tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#111827', margin: 0 }}>
            {mainTab === 'payments' ? 'Merchant Payments' : 'Supplier Settlements'}
          </h2>
          <span style={{ padding: '4px 12px', background: '#f3f4f6', color: '#374151', borderRadius: '9999px', fontSize: '13px', fontWeight: '500' }}>
            {mainTab === 'payments' ? paymentsPagination.total : settlementsPagination.total} total
          </span>
        </div>

        {loading ? (
          <div style={{ padding: '60px', textAlign: 'center' }}>
            <Loader2 size={32} className="animate-spin" style={{ color: '#6b7280', margin: '0 auto 16px' }} />
            <p style={{ color: '#6b7280' }}>Loading...</p>
          </div>
        ) : mainTab === 'payments' ? (
          payments.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ width: '64px', height: '64px', background: '#f3f4f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <CreditCard size={32} color="#6b7280" />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>No payments yet</h3>
              <p style={{ color: '#6b7280' }}>Merchant payments will appear here when they join group deals</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Merchant</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Deal</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Supplier</th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Qty</th>
                    <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Amount</th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Paid At</th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((payment, index) => (
                    <tr key={payment.id} style={{ borderBottom: index < payments.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: '600', color: '#111827' }}>
                          {payment.merchant?.firstName} {payment.merchant?.lastName}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>
                          {payment.merchant?.store?.name || payment.merchant?.email}
                        </div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: '500', color: '#374151' }}>{payment.deal?.title || 'N/A'}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{payment.deal?.productName}</div>
                      </td>
                      <td style={{ padding: '16px 20px', color: '#374151' }}>
                        {payment.deal?.supplier?.businessName || 'N/A'}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center', fontWeight: '500' }}>
                        {payment.quantity}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                        {formatCurrency(payment.amountPaid)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        {getStatusBadge(payment.status)}
                      </td>
                      <td style={{ padding: '16px 20px', color: '#6b7280', fontSize: '13px' }}>
                        {formatDate(payment.paidAt)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <button
                          onClick={() => viewPaymentDetails(payment)}
                          style={{
                            padding: '6px 10px', background: 'transparent', border: '1px solid #e5e7eb',
                            borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                            gap: '4px', fontSize: '12px', color: '#374151', margin: '0 auto'
                          }}
                        >
                          <Eye size={14} />View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : (
          settlements.length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center' }}>
              <div style={{ width: '64px', height: '64px', background: '#f3f4f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                <Wallet size={32} color="#6b7280" />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>No settlements yet</h3>
              <p style={{ color: '#6b7280' }}>Settlements are created when deals are completed or expired</p>
              <button
                onClick={runCleanup}
                disabled={processing}
                style={{
                  marginTop: '16px', padding: '10px 20px', background: '#f59e0b', color: 'white',
                  border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '500'
                }}
              >
                Check for Completed Deals
              </button>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Deal</th>
                    <th style={{ padding: '12px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Supplier</th>
                    <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Collected</th>
                    <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Platform Fee</th>
                    <th style={{ padding: '12px 20px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Supplier Gets</th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Status</th>
                    <th style={{ padding: '12px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280', textTransform: 'uppercase' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {settlements.map((settlement, index) => (
                    <tr key={settlement.id} style={{ borderBottom: index < settlements.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: '600', color: '#111827' }}>{settlement.deal?.title || 'N/A'}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{settlement.deal?.productName}</div>
                      </td>
                      <td style={{ padding: '16px 20px' }}>
                        <div style={{ fontWeight: '500', color: '#374151' }}>{settlement.supplier?.businessName || 'N/A'}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280' }}>{settlement.supplier?.email}</div>
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'right', fontWeight: '600', color: '#111827' }}>
                        {formatCurrency(settlement.totalCollected)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'right', fontWeight: '500', color: '#10b981' }}>
                        {formatCurrency(settlement.platformFee)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'right', fontWeight: '600', color: '#374151' }}>
                        {formatCurrency(settlement.supplierPayout)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        {getStatusBadge(settlement.status)}
                      </td>
                      <td style={{ padding: '16px 20px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', gap: '8px' }}>
                          <button
                            onClick={() => viewSettlementDetails(settlement)}
                            style={{
                              padding: '6px 10px', background: 'transparent', border: '1px solid #e5e7eb',
                              borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                              gap: '4px', fontSize: '12px', color: '#374151'
                            }}
                          >
                            <Eye size={14} />View
                          </button>
                          {settlement.status === 'PENDING' && (
                            <button
                              onClick={() => processSettlement(settlement.id)}
                              disabled={processing}
                              style={{
                                padding: '6px 10px', background: '#10b981', border: 'none',
                                borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center',
                                gap: '4px', fontSize: '12px', color: 'white'
                              }}
                            >
                              <CreditCard size={14} />Pay
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {/* Pagination */}
        {!loading && ((mainTab === 'payments' && paymentsPagination.total > 0) || (mainTab === 'settlements' && settlementsPagination.total > 0)) && (
          <div style={{ padding: '16px 20px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>
              Page {mainTab === 'payments' ? paymentsPagination.current : settlementsPagination.current} of {mainTab === 'payments' ? paymentsPagination.pages : settlementsPagination.pages}
            </span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => mainTab === 'payments'
                  ? setPaymentsPagination(p => ({ ...p, current: p.current - 1 }))
                  : setSettlementsPagination(p => ({ ...p, current: p.current - 1 }))
                }
                disabled={(mainTab === 'payments' ? paymentsPagination.current : settlementsPagination.current) === 1}
                style={{
                  padding: '8px 16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px',
                  cursor: 'pointer', fontSize: '13px', opacity: (mainTab === 'payments' ? paymentsPagination.current : settlementsPagination.current) === 1 ? 0.5 : 1
                }}
              >Previous</button>
              <button
                onClick={() => mainTab === 'payments'
                  ? setPaymentsPagination(p => ({ ...p, current: p.current + 1 }))
                  : setSettlementsPagination(p => ({ ...p, current: p.current + 1 }))
                }
                disabled={(mainTab === 'payments' ? paymentsPagination.current >= paymentsPagination.pages : settlementsPagination.current >= settlementsPagination.pages)}
                style={{
                  padding: '8px 16px', background: 'white', border: '1px solid #e5e7eb', borderRadius: '6px',
                  cursor: 'pointer', fontSize: '13px', opacity: (mainTab === 'payments' ? paymentsPagination.current >= paymentsPagination.pages : settlementsPagination.current >= settlementsPagination.pages) ? 0.5 : 1
                }}
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && selectedItem && (
        <div onClick={closeModal} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: 0 }}>
                {modalType === 'payment' ? 'Payment Details' : 'Settlement Details'}
              </h2>
              <button onClick={closeModal} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>

            <div style={{ padding: '24px' }}>
              {modalType === 'payment' ? (
                <>
                  <div style={{ marginBottom: '20px' }}>{getStatusBadge(selectedItem.status)}</div>
                  <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Amount Paid</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#059669' }}>{formatCurrency(selectedItem.amountPaid)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Quantity</div>
                        <div style={{ fontSize: '24px', fontWeight: '700', color: '#111827' }}>{selectedItem.quantity} units</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Merchant</div>
                      <div style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.merchant?.firstName} {selectedItem.merchant?.lastName}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Store</div>
                      <div style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.merchant?.store?.name || 'N/A'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Deal</div>
                      <div style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.deal?.title}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Supplier</div>
                      <div style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.deal?.supplier?.businessName}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Payment Reference</div>
                      <div style={{ fontSize: '14px', color: '#111827', fontFamily: 'monospace' }}>{selectedItem.paymentReference || 'N/A'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Paid At</div>
                      <div style={{ fontSize: '14px', color: '#111827' }}>{formatDate(selectedItem.paidAt)}</div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '20px' }}>{getStatusBadge(selectedItem.status)}</div>
                  <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '16px', marginBottom: '20px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Total Collected</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#111827' }}>{formatCurrency(selectedItem.totalCollected)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Platform Fee</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>{formatCurrency(selectedItem.platformFee)}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Supplier Gets</div>
                        <div style={{ fontSize: '20px', fontWeight: '700', color: '#374151' }}>{formatCurrency(selectedItem.supplierPayout)}</div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Deal</div>
                      <div style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.deal?.title}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Supplier</div>
                      <div style={{ fontSize: '14px', color: '#111827' }}>{selectedItem.supplier?.businessName}</div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              {modalType === 'settlement' && selectedItem.status === 'PENDING' && (
                <button
                  onClick={() => markAsSettled(selectedItem.id)}
                  disabled={processing}
                  style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}
                >Mark as Settled</button>
              )}
              <button onClick={closeModal} style={{ padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .animate-spin { animation: spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default GroupDealSettlements;
